import { useEffect, useMemo, useState } from 'react';
import useReadNotifications from '~/components/Notifications/hooks/useReadNotifications';

import type { NotificationType } from '~/interfaces/notification';
import { useNonRevalidatingFetcher } from '../useNonRevalidatingFetcher';

interface ReturnProps {
  notificationsCount: number;
  notifications: NotificationType[];
  isLoading: boolean;
  fetchMoreNotifications: () => void;
  hasMoreToFetch: boolean;
}

type FetcherData = {
  notifications: NotificationType[];
  pagination: {
    page: number;
    total: number;
    per_page: number;
  };
};

const usePaginatedNotifications = (): ReturnProps => {
  const fetcher = useNonRevalidatingFetcher<() => Promise<FetcherData>>();
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [page, setPage] = useState(0);
  const fetchedData = fetcher.data;
  const paginationTotal = fetchedData?.pagination?.total || 0;
  const currentPage = fetchedData?.pagination?.page || 0;

  const { readNotifications, readNotificationsCount } = useReadNotifications();
  const readNotificationsLength = readNotifications.length;
  const hasMoreToFetch = notifications.length + readNotificationsCount < paginationTotal;

  const fetchNotifications = () => {
    const searchParams = new URLSearchParams();
    searchParams.set('page', String(page + 1));

    fetcher.load(`/notifications?${searchParams.toString()}`);
  };

  const filterReadNotifications = (notifications: NotificationType[]) => {
    if (readNotificationsCount === 0) {
      return notifications;
    }

    return notifications.filter((notification) => {
      return !readNotifications.includes(notification.id);
    });
  };

  const notificationsCount = useMemo(() => {
    let count = fetchedData?.pagination?.total || 0;
    count -= readNotifications.length;

    if (count < 0) {
      return 0;
    }

    return count;
  }, [fetchedData, readNotificationsCount]);

  // Fetch initial notifications
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Refilter notifications when new notifications have been read
  useEffect(() => {
    setNotifications(filterReadNotifications(notifications));
  }, [readNotificationsLength]);

  useEffect(() => {
    if (fetchedData) {
      setPage(page + 1);
      const updatedNotifications = [...notifications, ...fetchedData.notifications];
      setNotifications(filterReadNotifications(updatedNotifications));
    }
  }, [currentPage]);

  return {
    notificationsCount,
    notifications,
    isLoading: fetcher?.state !== 'idle',
    fetchMoreNotifications: fetchNotifications,
    hasMoreToFetch,
  };
};

export default usePaginatedNotifications;
